import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface ContextMenuItem {
  label: string;
  onSelect: () => void;
  tone?: 'default' | 'danger';
}

interface ContextMenuProps {
  isOpen: boolean;
  x: number;
  y: number;
  label: string;
  items: ContextMenuItem[];
  onClose: () => void;
}

const VIEWPORT_MARGIN = 8;
const FALLBACK_MENU_WIDTH = 220;
const FALLBACK_MENU_HEIGHT = 192;

export const ContextMenu: React.FC<ContextMenuProps> = ({ isOpen, x, y, label, items, onClose }) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [position, setPosition] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    const menuWidth = menuRef.current?.offsetWidth || FALLBACK_MENU_WIDTH;
    const menuHeight = menuRef.current?.offsetHeight || FALLBACK_MENU_HEIGHT;
    const left = Math.min(Math.max(x, VIEWPORT_MARGIN), window.innerWidth - menuWidth - VIEWPORT_MARGIN);
    const top = Math.min(Math.max(y, VIEWPORT_MARGIN), window.innerHeight - menuHeight - VIEWPORT_MARGIN);

    setPosition({ left, top });
  }, [isOpen, x, y]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const focusId = window.requestAnimationFrame(() => {
      itemRefs.current[0]?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusId);
    };
  }, [isOpen, items.length]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (menuRef.current && event.target instanceof Node && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleViewportChange = () => {
      onClose();
    };

    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [isOpen, onClose]);

  const focusItemAt = (index: number) => {
    if (items.length === 0) {
      return;
    }

    const normalizedIndex = (index + items.length) % items.length;
    itemRefs.current[normalizedIndex]?.focus();
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const activeIndex = itemRefs.current.findIndex((node) => node === document.activeElement);

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusItemAt(activeIndex >= 0 ? activeIndex + 1 : 0);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusItemAt(activeIndex >= 0 ? activeIndex - 1 : items.length - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusItemAt(0);
        break;
      case 'End':
        event.preventDefault();
        focusItemAt(items.length - 1);
        break;
      default:
        break;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={menuRef}
      className="context-menu"
      role="menu"
      aria-label={label}
      style={{ left: position.left, top: position.top }}
      onKeyDown={handleMenuKeyDown}
    >
      {items.map((item, index) => (
        <button
          key={item.label}
          ref={(node) => {
            itemRefs.current[index] = node;
          }}
          type="button"
          role="menuitem"
          className={`context-menu-item${item.tone === 'danger' ? ' is-danger' : ''}`}
          onClick={() => {
            item.onSelect();
            onClose();
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};